import AppLayout from '../layout/AppLayout';
import { HeaderProjects as Header } from '../layout/Header';
import useUser, { getUser } from '../authentication/context/useUser';
import styled from '@emotion/styled';
import { Navigate, useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

const SuccessContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem;
  background: #4CAF50;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
`;

const Title = styled.h1`
  text-align: center;
  color: #333;
  margin-bottom: 1.5rem;
`;

const Message = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const NextSteps = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;
`;

const NextStepsTitle = styled.h2`
  color: #333;
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const NextStepsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NextStepsItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: #666;

  &:before {
    content: "✓";
    color: #4CAF50;
    margin-right: 0.5rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  gap: 1rem;
`;

const PendingIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem;
  background: #FFA726;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const PendingMessage = styled(Message)`
  max-width: 600px;
  margin: 0 auto 2rem;
`;

const StatusInfo = styled.div`
  background: #fff3e0;
  padding: 1.5rem;
  border-radius: 8px;
  margin: 2rem 0;
  border-left: 4px solid #FFA726;
`;

const StatusTitle = styled.h3`
  color: #E65100;
  margin-bottom: 0.5rem;
`;

const StatusText = styled.p`
  color: #666;
  margin: 0;
  line-height: 1.6;
`;

const FailedIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem;
  background: #EF5350;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
`;

const FailedMessage = styled(Message)`
  max-width: 600px;
  margin: 0 auto 2rem;
`;

const ErrorInfo = styled.div`
  background: #ffebee;
  padding: 1.5rem;
  border-radius: 8px;
  margin: 2rem 0;
  border-left: 4px solid #EF5350;
`;

const ErrorTitle = styled.h3`
  color: #C62828;
  margin-bottom: 0.5rem;
`;

const ErrorText = styled.p`
  color: #666;
  margin: 0;
  line-height: 1.6;
`;

const CheckoutSuccess = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);

  return (
    <AppLayout header={<Header />}>
      <SuccessContainer>
        <SuccessIcon>✓</SuccessIcon>
        <Title>{t('common:subscription.checkoutPage.success.title')}</Title>
        <Message>
          {t('common:subscription.checkoutPage.success.message', { firstName: user?.firstName })}
        </Message>

        <NextSteps>
          <NextStepsTitle>{t('common:subscription.checkoutPage.success.nextSteps.title')}</NextStepsTitle>
          <NextStepsList>
            <NextStepsItem>{t('common:subscription.checkoutPage.success.nextSteps.checkEmail')}</NextStepsItem>
            <NextStepsItem>{t('common:subscription.checkoutPage.success.nextSteps.exploreFeatures')}</NextStepsItem>
            <NextStepsItem>{t('common:subscription.checkoutPage.success.nextSteps.configureAccount')}</NextStepsItem>
            <NextStepsItem>{t('common:subscription.checkoutPage.success.nextSteps.inviteCollaborators')}</NextStepsItem>
          </NextStepsList>
        </NextSteps>

        <ButtonContainer>
          <Button onClick={() => navigate('/')}>
            {t('common:subscription.checkoutPage.success.button')}
          </Button>
        </ButtonContainer>
      </SuccessContainer>
    </AppLayout>
  );
};

const CheckoutPending = () => {
  const { t } = useTranslation(['common']);

  return (
    <AppLayout header={<Header />}>
      <SuccessContainer>
        <PendingIcon>⏳</PendingIcon>
        <Title>{t('common:subscription.checkoutPage.pending.title')}</Title>
        <PendingMessage>
          {t('common:subscription.checkoutPage.pending.message')}
        </PendingMessage>

        <StatusInfo>
          <StatusTitle>{t('common:subscription.checkoutPage.pending.status.title')}</StatusTitle>
          <StatusText>
            {t('common:subscription.checkoutPage.pending.status.description')}
          </StatusText>
        </StatusInfo>

        <NextSteps>
          <NextStepsTitle>{t('common:subscription.checkoutPage.pending.nextSteps.title')}</NextStepsTitle>
          <NextStepsList>
            <NextStepsItem>{t('common:subscription.checkoutPage.pending.nextSteps.checkEmail')}</NextStepsItem>
            <NextStepsItem>{t('common:subscription.checkoutPage.pending.nextSteps.checkSpace')}</NextStepsItem>
            <NextStepsItem>{t('common:subscription.checkoutPage.pending.nextSteps.prepareProject')}</NextStepsItem>
          </NextStepsList>
        </NextSteps>

        <ButtonContainer>
          <Button onClick={() => window.location.reload()}>
            {t('common:subscription.checkoutPage.pending.button')}
          </Button>
        </ButtonContainer>
      </SuccessContainer>
    </AppLayout>
  );
};

const CheckoutFailed = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);

  return (
    <AppLayout header={<Header />}>
      <SuccessContainer>
        <FailedIcon>✕</FailedIcon>
        <Title>{t('common:subscription.checkoutPage.failed.title')}</Title>
        <FailedMessage>
          {t('common:subscription.checkoutPage.failed.message')}
        </FailedMessage>

        <ErrorInfo>
          <ErrorTitle>{t('common:subscription.checkoutPage.failed.error.title')}</ErrorTitle>
          <ErrorText>
            {t('common:subscription.checkoutPage.failed.error.description')}
          </ErrorText>
        </ErrorInfo>

        <NextSteps>
          <NextStepsTitle>{t('common:subscription.checkoutPage.failed.nextSteps.title')}</NextStepsTitle>
          <NextStepsList>
            <NextStepsItem>{t('common:subscription.checkoutPage.failed.nextSteps.checkCard')}</NextStepsItem>
            <NextStepsItem>{t('common:subscription.checkoutPage.failed.nextSteps.tryOtherCard')}</NextStepsItem>
            <NextStepsItem>{t('common:subscription.checkoutPage.failed.nextSteps.contactBank')}</NextStepsItem>
          </NextStepsList>
        </NextSteps>

        <ButtonContainer>
          <Button onClick={() => navigate('/checkout')}>
            {t('common:subscription.checkoutPage.failed.retryButton')}
          </Button>
          <Button onClick={() => navigate('/')} color="secondary">
            {t('common:subscription.checkoutPage.failed.homeButton')}
          </Button>
        </ButtonContainer>
      </SuccessContainer>
    </AppLayout>
  );
};

const CheckoutStatus = () => {
  const { data: user } = useQuery('user', getUser, {
    useErrorBoundary: true,
    refetchInterval: 4000,
  });

  if (!user) {
    return null;
  }

  if (user?.subscriptionStatus === "processing") {
    return <CheckoutPending />;
  }

  if (user?.subscriptionStatus === "success") {
    return <CheckoutSuccess />;
  }

  if (user?.subscriptionStatus === "failed") {
    return <CheckoutFailed />;
  }

  return <CheckoutPending />;
};

export default CheckoutStatus;
